import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import {
  StatementInputSource,
  StatementInputType,
  SurveyListItem,
  GradeRevisionListItem,
  SurveyResults
} from '../../models/statement.models';
import { StatementGeneratorService } from '../../services/statement-generator.service';
import { GradeService } from '../../services/grade.service';
import { SurveyService } from '../../services/surveys.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ModalAddAttachmentComponent } from '../modal-add-attachment/modal-add-attachment.component';

interface InputTab {
  id: StatementInputType;
  label: string;
  icon: string;
}

@Component({
    selector: 'app-modal-select-documents',
    templateUrl: './modal-select-documents.component.html',
    styleUrls: ['./modal-select-documents.component.scss'],
    standalone: false
})
export class ModalSelectDocumentsComponent implements OnInit {

  // Tab configuration
  inputTabs: InputTab[] = [
    { id: 'survey', label: 'Surveys', icon: 'poll' },
    { id: 'pdf', label: 'PDF Documents', icon: 'picture_as_pdf' },
    { id: 'pubmed', label: 'PubMed', icon: 'article' },
    { id: 'literature_review', label: 'Literature Review', icon: 'library_books' }
  ];

  activeTab: StatementInputType = 'survey';
  activeTabIndex: number = 0;
  selectedInputs: StatementInputSource[] = [];

  // Survey tab data
  availableSurveys: SurveyListItem[] = [];
  selectedSurveys: Set<number> = new Set();
  loadingSurveys = false;
  surveyResultsCache: Map<number, SurveyResults> = new Map();

  // PDF tab data
  storedPDFs: any[] = [];
  uploadedPDFs: StatementInputSource[] = [];
  selectedPDFs: Set<number> = new Set();
  loadingPDFs = false;
  uploadingPDF = false;
  uploadProgress = 0;

  // PubMed tab data
  storedPubMedDocs: any[] = [];
  selectedPubMedIds: Set<string> = new Set();
  loadingPubMedDocs = false;

  // Literature Review tab data
  availableLiteratureReviews: GradeRevisionListItem[] = [];
  selectedLiteratureReviews: Set<number> = new Set();
  loadingLiteratureReviews = false;
  revisionDocumentsCache: Map<number, any[]> = new Map();

  // Project data from parent
  projectId: number;
  projectData: any;

  constructor(
    public dialogRef: MatDialogRef<ModalSelectDocumentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private statementService: StatementGeneratorService,
    private gradeService: GradeService,
    private surveyService: SurveyService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.projectId = data.projectId;
    this.projectData = data.projectData;
    if (data.selectedInputs) {
      this.selectedInputs = [...data.selectedInputs];
    }
  }

  ngOnInit(): void {
    this.loadSurveys();
  }

  onTabChange(event: any): void {
    console.log('Tab change event:', event);

    // Map tab index to input type
    const tabIndex = event.index !== undefined ? event.index : this.activeTabIndex;
    console.log('Active tab index:', tabIndex);

    const tabTypes: StatementInputType[] = ['survey', 'pdf', 'pubmed', 'literature_review'];
    this.activeTab = tabTypes[tabIndex] || 'survey';

    console.log('Active tab type:', this.activeTab);

    switch (this.activeTab) {
      case 'survey':
        if (this.availableSurveys.length === 0 && !this.loadingSurveys) {
          console.log('Loading surveys...');
          this.loadSurveys();
        }
        break;
      case 'pdf':
        if (this.storedPDFs.length === 0 && !this.loadingPDFs) {
          console.log('Loading stored PDFs...');
          this.loadStoredPDFs();
        }
        break;
      case 'pubmed':
        if (this.storedPubMedDocs.length === 0 && !this.loadingPubMedDocs) {
          console.log('Loading stored PubMed documents...');
          this.loadStoredPubMedDocuments();
        }
        break;
      case 'literature_review':
        if (this.availableLiteratureReviews.length === 0 && !this.loadingLiteratureReviews) {
          console.log('Loading literature reviews...');
          this.loadLiteratureReviews();
        }
        break;
    }
  }

  // SURVEY TAB METHODS
  loadSurveys(): void {
    if (!this.projectId) return;

    this.loadingSurveys = true;

    // Use the existing SurveyService with correct endpoint
    this.surveyService.getSurveysByProjectId(
      this.projectId.toString(),
      (response: any) => {
        console.log('Raw survey response:', response);

        // Handle different response formats
        let surveys: any[] = [];
        if (Array.isArray(response)) {
          surveys = response;
        } else if (response && Array.isArray(response.data)) {
          surveys = response.data;
        } else if (response && Array.isArray(response.surveys)) {
          surveys = response.surveys;
        } else {
          console.warn('Unexpected survey response format:', response);
          surveys = [];
        }

        console.log('Extracted surveys array:', surveys);
        console.log('Survey statuses:', surveys.map(s => ({ id: s.id, title: s.title, status: s.status })));

        // Map all surveys (don't filter by status yet - let's see what we have)
        this.availableSurveys = surveys.map(s => ({
          id: s.id,
          title: s.title || s.name || 'Untitled Survey',
          description: s.description || '',
          status: s.status,
          members: s.team_members || s.members || [],
          questions_count: s.questions?.length || s.questions_count || 0,
          responses_count: s.responses_count || 0,
          completion_rate: s.completion_rate || this.calculateCompletionRate(s),
          created_at: s.created_at,
          updated_at: s.updated_at,
          closed_at: s.closed_at || null
        }));

        this.loadingSurveys = false;
        console.log('All processed surveys:', this.availableSurveys);
        console.log('Available surveys count:', this.availableSurveys.length);
      },
      (error) => {
        console.error('Error loading surveys:', error);
        this.loadingSurveys = false;
        this.showMessage('modals.select-inputs.error-loading-surveys', 'error');
      }
    );
  }

  /**
   * Calculate completion rate from survey data
   */
  private calculateCompletionRate(survey: any): number {
    if (!survey.team_members || survey.team_members.length === 0) return 0;
    const completedCount = survey.team_members.filter((m: any) => m.completed || m.status === 'completed').length;
    return completedCount / survey.team_members.length;
  }

  toggleSurveySelection(surveyId: number): void {
    if (this.selectedSurveys.has(surveyId)) {
      this.selectedSurveys.delete(surveyId);
      this.removeInputById('survey', surveyId.toString());
    } else {
      this.selectedSurveys.add(surveyId);
      const survey = this.availableSurveys.find(s => s.id === surveyId);
      if (survey) {
        // Fetch survey results to include in the data
        this.fetchSurveyResultsAndAdd(survey);
      }
    }
  }

  /**
   * Fetch survey results and add to selected inputs
   */
  private fetchSurveyResultsAndAdd(survey: SurveyListItem): void {
    // Check cache first
    if (this.surveyResultsCache.has(survey.id)) {
      this.addSurveyInput(survey, this.surveyResultsCache.get(survey.id)!);
      return;
    }

    // Fetch from API using SurveyService
    this.surveyService.surveyResponses(
      survey.id.toString(),
      1,
      10,
      (results: any) => {
        console.log('Fetched survey results:', results);
        const formattedResults: SurveyResults = {
          questions: results.questions || []
        };
        this.surveyResultsCache.set(survey.id, formattedResults);
        this.addSurveyInput(survey, formattedResults);
      },
      (error) => {
        console.error('Error fetching survey results:', error);
        // Add without results if fetch fails
        this.addSurveyInput(survey, null);
        this.showMessage('modals.select-inputs.error-loading-survey-results', 'error');
      }
    );
  }

  /**
   * Add survey input with results data
   */
  private addSurveyInput(survey: SurveyListItem, results: SurveyResults | null): void {
    this.addInput({
      id: `survey-${survey.id}`,
      type: 'survey',
      name: survey.title,
      data: {
        ...survey,
        results: results
      },
      metadata: {
        surveyId: survey.id,
        participants: survey.members?.length || 0,
        uploadDate: new Date(survey.updated_at || survey.created_at)
      }
    });
  }

  /**
   * View survey details in a dialog
   */
  viewSurveyDetails(surveyId: number, event: Event): void {
    event.stopPropagation();

    const survey = this.availableSurveys.find(s => s.id === surveyId);
    if (!survey) return;

    // Check cache first
    if (this.surveyResultsCache.has(surveyId)) {
      this.openSurveyDetailsDialog(survey, this.surveyResultsCache.get(surveyId)!);
      return;
    }

    // Fetch results using SurveyService
    this.surveyService.surveyResponses(
      surveyId.toString(),
      1,
      10,
      (results: any) => {
        const formattedResults: SurveyResults = {
          questions: results.questions || []
        };
        this.surveyResultsCache.set(surveyId, formattedResults);
        this.openSurveyDetailsDialog(survey, formattedResults);
      },
      (error) => {
        console.error('Error fetching survey details:', error);
        this.showMessage('modals.select-inputs.error-loading-survey-details', 'error');
      }
    );
  }

  /**
   * Open survey details dialog (placeholder - implement dialog component)
   */
  private openSurveyDetailsDialog(survey: SurveyListItem, results: SurveyResults): void {
    // TODO: Implement dedicated survey details dialog component
    console.log('Survey details:', survey, results);
    this.showMessage('Survey: ' + survey.title, 'success');
  }

  /**
   * Get completion percentage formatted
   */
  getCompletionPercentage(survey: SurveyListItem): number {
    return Math.round((survey.completion_rate || 0) * 100);
  }

  /**
   * Get completion badge class based on completion rate
   */
  getCompletionBadgeClass(survey: SurveyListItem): string {
    const percentage = this.getCompletionPercentage(survey);
    if (percentage >= 80) return 'badge-success';
    if (percentage >= 50) return 'badge-warning';
    return 'badge-danger';
  }

  // PDF TAB METHODS
  loadStoredPDFs(): void {
    if (!this.projectId) return;

    this.loadingPDFs = true;

    this.statementService.getProjectPDFDocuments(
      this.projectId,
      (pdfs: any[]) => {
        console.log('Raw PDF documents response:', pdfs);

        this.storedPDFs = (pdfs || []).map(pdf => ({
          id: pdf.id,
          file_name: pdf.file_name || pdf.name || 'Untitled Document',
          file_path: pdf.file_path,
          file_size: pdf.file_size || 0,
          upload_date: pdf.upload_date || pdf.created_at,
          extracted_text: pdf.extracted_text || null,
          pages_count: pdf.pages_count || null
        }));

        this.loadingPDFs = false;
        console.log('Stored PDFs loaded:', this.storedPDFs);
      },
      (error) => {
        console.error('Error loading stored PDFs:', error);
        this.loadingPDFs = false;
        this.showMessage('modals.select-inputs.error-loading-pdfs', 'error');
      }
    );
  }

  toggleStoredPDFSelection(pdfId: number): void {
    if (this.selectedPDFs.has(pdfId)) {
      this.selectedPDFs.delete(pdfId);
      this.removeInputById('pdf', pdfId.toString());
    } else {
      this.selectedPDFs.add(pdfId);
      const pdf = this.storedPDFs.find(p => p.id === pdfId);
      if (pdf) {
        this.addInput({
          id: `pdf-${pdf.id}`,
          type: 'pdf',
          name: pdf.file_name,
          data: pdf,
          metadata: {
            uploadDate: new Date(pdf.upload_date),
            size: this.formatFileSize(pdf.file_size),
            fileType: 'PDF'
          }
        });
      }
    }
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        this.uploadPDF(file);
      });
    }
  }

  uploadPDF(file: File): void {
    if (!this.projectId) return;

    this.uploadingPDF = true;
    this.uploadProgress = 0;

    this.statementService.uploadPDFDocument(
      file,
      this.projectId,
      (data) => {
        const input: StatementInputSource = {
          id: `pdf-${Date.now()}`,
          type: 'pdf',
          name: file.name,
          data: data,
          metadata: {
            uploadDate: new Date(),
            size: this.formatFileSize(file.size),
            fileType: 'PDF'
          }
        };
        this.uploadedPDFs.push(input);
        this.addInput(input);
        this.uploadingPDF = false;
        this.uploadProgress = 100;
      },
      (error) => {
        console.error('Error uploading PDF:', error);
        this.uploadingPDF = false;
        this.uploadProgress = 0;
      }
    );
  }

  removePDF(index: number): void {
    const pdf = this.uploadedPDFs[index];
    this.uploadedPDFs.splice(index, 1);
    this.removeInputById('pdf', pdf.id);
  }

  // PUBMED TAB METHODS
  loadStoredPubMedDocuments(): void {
    if (!this.projectId) return;

    this.loadingPubMedDocs = true;

    this.statementService.getProjectPubMedDocuments(
      this.projectId,
      (documents: any[]) => {
        console.log('Raw PubMed documents response:', documents);

        this.storedPubMedDocs = (documents || []).map(doc => ({
          id: doc.id,
          pmid: doc.pmid,
          title: doc.title || 'Untitled Article',
          authors: doc.authors || '',
          abstract: doc.abstract || '',
          year: doc.year || doc.publication_year,
          doi: doc.doi || null,
          url: doc.url || `https://pubmed.ncbi.nlm.nih.gov/${doc.pmid}/`,
          journal: doc.journal || null,
          added_at: doc.added_at || doc.created_at
        }));

        this.loadingPubMedDocs = false;
        console.log('Stored PubMed documents loaded:', this.storedPubMedDocs);
      },
      (error) => {
        console.error('Error loading stored PubMed documents:', error);
        this.loadingPubMedDocs = false;
        this.showMessage('modals.select-inputs.error-loading-pubmed-docs', 'error');
      }
    );
  }

  toggleStoredPubMedSelection(docId: number): void {
    const doc = this.storedPubMedDocs.find(d => d.id === docId);
    if (!doc) return;

    const pmid = doc.pmid;
    if (this.selectedPubMedIds.has(pmid)) {
      this.selectedPubMedIds.delete(pmid);
      this.removeInputById('pubmed', `pubmed-${pmid}`);
    } else {
      this.selectedPubMedIds.add(pmid);
      this.addInput({
        id: `pubmed-${pmid}`,
        type: 'pubmed',
        name: doc.title,
        data: doc,
        metadata: {
          url: doc.url,
          uploadDate: new Date(doc.added_at)
        }
      });
    }
  }

  /**
   * Open the existing PubMed attachment modal to search and add papers
   */
  openPubMedAttachmentModal(): void {
    const dialogRef = this.dialog.open(ModalAddAttachmentComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        title: 'modals.add-attachment.pubmed-title',
        attachmentType: 'PUBMED'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.attachmentObject) {
        const articles = Array.isArray(result.attachmentObject)
          ? result.attachmentObject
          : [result.attachmentObject];

        articles.forEach(pubmedArticle => {
          console.log('PubMed article selected:', pubmedArticle);
          // Save to database and add to selection
          this.savePubMedToDatabase(pubmedArticle);
        });
      }
    });
  }

  /**
   * Save PubMed article to database and add to selected inputs
   */
  private savePubMedToDatabase(article: any): void {
    if (!this.projectId) {
      console.error('No project ID available');
      return;
    }

    // Prepare PubMed data for saving
    const pubmedData = {
      pmid: article.PMID || article.pmid,
      title: article.ArticleTitle || article.title,
      authors: this.formatAuthors(article.Authors || article.authors),
      abstract: article.Abstract || article.abstract || '',
      year: article.PubYear || article.year || new Date().getFullYear(),
      journal: article.Source || article.journal || '',
      doi: article.DOI || article.doi || null,
      url: `https://pubmed.ncbi.nlm.nih.gov/${article.PMID || article.pmid}/`
    };

    // Save to database
    this.statementService.savePubMedDocumentToProject(
      this.projectId,
      pubmedData,
      (savedDoc) => {
        console.log('PubMed document saved:', savedDoc);

        // Add to stored documents list
        this.storedPubMedDocs.unshift({
          id: savedDoc.id,
          pmid: pubmedData.pmid,
          title: pubmedData.title,
          authors: pubmedData.authors,
          abstract: pubmedData.abstract,
          year: pubmedData.year,
          doi: pubmedData.doi,
          url: pubmedData.url,
          journal: pubmedData.journal,
          added_at: new Date().toISOString()
        });

        // Automatically select it
        this.toggleStoredPubMedSelection(savedDoc.id);

        this.showMessage('modals.select-inputs.pubmed-saved', 'success');
      },
      (error) => {
        console.error('Error saving PubMed document:', error);

        // If it already exists, just add it to selection without error
        if (error?.status === 409 || error?.error?.message?.includes('already exists')) {
          console.log('PubMed article already exists, adding to selection');
          // Try to find it in stored docs and select it
          const existingDoc = this.storedPubMedDocs.find(d => d.pmid === pubmedData.pmid);
          if (existingDoc) {
            this.toggleStoredPubMedSelection(existingDoc.id);
          }
          this.showMessage('modals.select-inputs.pubmed-already-exists', 'warning');
        } else {
          this.showMessage('modals.select-inputs.error-saving-pubmed', 'error');
        }
      }
    );
  }

  /**
   * Format authors array/string to string
   */
  private formatAuthors(authors: any): string {
    if (!authors) return '';
    if (typeof authors === 'string') return authors;
    if (Array.isArray(authors)) {
      return authors.join(', ');
    }
    return '';
  }


  // LITERATURE REVIEW TAB METHODS
  loadLiteratureReviews(): void {
    if (!this.projectId) return;

    this.loadingLiteratureReviews = true;

    // Use the existing GradeService
    this.gradeService.getRevisionsByProjectId(this.projectId).subscribe({
      next: (revisions: any[]) => {
        console.log('Raw GRADE revisions response:', revisions);
        console.log('Revisions count:', revisions?.length);
        console.log('Revision statuses:', revisions?.map(r => ({ id: r.id, title: r.title, status: r.status })));

        // Map all revisions (don't filter by status - let's see what we have)
        this.availableLiteratureReviews = (revisions || []).map(r => ({
          id: r.id,
          title: r.title || 'Untitled Revision',
          description: r.description || '',
          status: 'completed' as const,
          pico_question: r.pico_question || null,
          search_strategy: r.search_strategy || r.search_query || '',
          keywords: r.keywords || r.search_query || '',
          inclusion_criteria: r.inclusion_criteria || null,
          exclusion_criteria: r.exclusion_criteria || null,
          documents: r.documents || [],
          documents_count: r.documents?.length || r.documents_count || 0,
          summary: r.summary || '',
          overall_quality: (r.overall_quality || 'moderate') as 'high' | 'moderate' | 'low',
          created_by_user_id: r.created_by_user_id || r.created_by || 0,
          created_at: r.created_at,
          updated_at: r.updated_at,
          completed_at: r.completed_at || r.updated_at
        }));

        this.loadingLiteratureReviews = false;
        console.log('All processed GRADE revisions:', this.availableLiteratureReviews);
        console.log('Available revisions count:', this.availableLiteratureReviews.length);
      },
      error: (error) => {
        console.error('Error loading GRADE revisions:', error);
        this.loadingLiteratureReviews = false;
        this.showMessage('modals.select-inputs.error-loading-revisions', 'error');
      }
    });
  }

  toggleLiteratureReviewSelection(revisionId: number): void {
    if (this.selectedLiteratureReviews.has(revisionId)) {
      this.selectedLiteratureReviews.delete(revisionId);
      this.removeInputById('literature_review', revisionId.toString());
    } else {
      this.selectedLiteratureReviews.add(revisionId);
      const revision = this.availableLiteratureReviews.find(r => r.id === revisionId);
      if (revision) {
        // Fetch detailed documents for the revision
        this.fetchRevisionDocumentsAndAdd(revision);
      }
    }
  }

  /**
   * Fetch GRADE revision documents and add to selected inputs
   */
  private fetchRevisionDocumentsAndAdd(revision: GradeRevisionListItem): void {
    // Check cache first
    if (this.revisionDocumentsCache.has(revision.id)) {
      this.addRevisionInput(revision, this.revisionDocumentsCache.get(revision.id)!);
      return;
    }

    // If revision already has documents, use them
    if (revision.documents && revision.documents.length > 0) {
      this.revisionDocumentsCache.set(revision.id, revision.documents);
      this.addRevisionInput(revision, revision.documents);
      return;
    }

    // Fetch from API using GradeService
    this.gradeService.getDocuments({ grade_revision_id: revision.id }).subscribe({
      next: (documents) => {
        console.log('Fetched revision documents:', documents);
        this.revisionDocumentsCache.set(revision.id, documents);
        this.addRevisionInput(revision, documents);
      },
      error: (error) => {
        console.error('Error fetching revision documents:', error);
        // Add with existing documents if fetch fails
        this.addRevisionInput(revision, revision.documents || []);
        this.showMessage('modals.select-inputs.error-loading-documents', 'error');
      }
    });
  }

  /**
   * Add revision input with documents data
   */
  private addRevisionInput(revision: GradeRevisionListItem, documents: any[]): void {
    this.addInput({
      id: `literature-${revision.id}`,
      type: 'literature_review',
      name: revision.title,
      data: {
        ...revision,
        documents: documents
      },
      metadata: {
        gradeRevisionId: revision.id,
        uploadDate: new Date(revision.updated_at || revision.created_at)
      }
    });
  }

  /**
   * View revision details in a dialog
   */
  viewRevisionDetails(revisionId: number, event: Event): void {
    event.stopPropagation();

    const revision = this.availableLiteratureReviews.find(r => r.id === revisionId);
    if (!revision) return;

    // Check cache first
    if (this.revisionDocumentsCache.has(revisionId)) {
      this.openRevisionDetailsDialog(revision, this.revisionDocumentsCache.get(revisionId)!);
      return;
    }

    // If revision already has documents, use them
    if (revision.documents && revision.documents.length > 0) {
      this.revisionDocumentsCache.set(revisionId, revision.documents);
      this.openRevisionDetailsDialog(revision, revision.documents);
      return;
    }

    // Fetch documents using GradeService
    this.gradeService.getDocuments({ grade_revision_id: revisionId }).subscribe({
      next: (documents) => {
        this.revisionDocumentsCache.set(revisionId, documents);
        this.openRevisionDetailsDialog(revision, documents);
      },
      error: (error) => {
        console.error('Error fetching revision details:', error);
        this.showMessage('modals.select-inputs.error-loading-revision-details', 'error');
      }
    });
  }

  /**
   * Open revision details dialog (placeholder - implement dialog component)
   */
  private openRevisionDetailsDialog(revision: GradeRevisionListItem, documents: any[]): void {
    // TODO: Implement dedicated revision details dialog component
    console.log('Revision details:', revision, documents);
    this.showMessage('Revision: ' + revision.title + ' (' + documents.length + ' documents)', 'success');
  }

  /**
   * Get quality badge class for GRADE revisions
   */
  getQualityBadgeClass(quality: string): string {
    switch (quality?.toLowerCase()) {
      case 'high':
        return 'badge-success';
      case 'moderate':
        return 'badge-warning';
      case 'low':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  /**
   * Get level of evidence badge class
   */
  getLevelOfEvidenceBadgeClass(level: string): string {
    switch (level?.toLowerCase()) {
      case '1a':
        return 'badge-success';
      case '1b':
        return 'badge-success';
      case '2a':
        return 'badge-info';
      case '2b':
        return 'badge-warning';
      case '3':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  // COMMON METHODS
  addInput(input: StatementInputSource): void {
    const existingIndex = this.selectedInputs.findIndex(i => i.id === input.id);
    if (existingIndex === -1) {
      this.selectedInputs.push(input);
    } else {
      this.selectedInputs[existingIndex] = input;
    }
  }

  removeInputById(type: StatementInputType, id: string): void {
    const fullId = id.includes('-') ? id : `${type}-${id}`;
    this.selectedInputs = this.selectedInputs.filter(i => i.id !== fullId && i.id !== id);
  }

  removeInput(index: number): void {
    const input = this.selectedInputs[index];
    this.selectedInputs.splice(index, 1);

    // Update type-specific selections
    if (input.type === 'survey' && input.metadata?.surveyId) {
      this.selectedSurveys.delete(input.metadata.surveyId);
    } else if (input.type === 'pubmed') {
      const pmid = input.id.replace('pubmed-', '');
      this.selectedPubMedIds.delete(pmid);
    } else if (input.type === 'literature_review' && input.metadata?.gradeRevisionId) {
      this.selectedLiteratureReviews.delete(input.metadata.gradeRevisionId);
    }
  }

  getInputIcon(type: StatementInputType): string {
    const tab = this.inputTabs.find(t => t.id === type);
    return tab ? tab.icon : 'insert_drive_file';
  }

  getInputTypeLabel(type: StatementInputType): string {
    const tab = this.inputTabs.find(t => t.id === type);
    return tab ? tab.label : type;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  confirmSelection(): void {
    this.dialogRef.close({
      selectedInputs: this.selectedInputs
    });
  }

  get hasSelections(): boolean {
    return this.selectedInputs.length > 0;
  }

  /**
   * Show snackbar message
   */
  private showMessage(messageKey: string, type: 'success' | 'error' | 'warning'): void {
    const message = this.translate.instant(messageKey);
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
